import React, { useState, useEffect, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { Activity, ActivityForm, ActivityVariant, Currency, PriceUnit } from '../types/Activity';
import { fetchAllActivities, createActivity, updateActivity, deleteActivity, getActivityFull } from '../api/Activities';
import { loadCityLocations } from '../api/Location';
import { LocationOption } from '../types/Location';
import '../css/ActivitiesPage.css';
import Layout from '../components/Layout';
import * as FiIcons from "react-icons/fi";
const FiTrash2 = FiIcons.FiTrash2 as unknown as React.FC;

const ActivitiesPage: React.FC = () => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [formData, setFormData] = useState<ActivityForm>({
    slug: '',
    name: '',
    description: '',
    duration_minutes: undefined,
    start_time: '',
    remarks: '',
    rules: '',
    picture_url: '',
    city: '',
    city_code: '',
    category: '',
    currency: 'INR',
    is_active: true,
    variants: []
  });

  const [variants, setVariants] = useState<ActivityVariant[]>([]);

  const [filters, setFilters] = useState({
    city: '',
    city_code: '',
    category: '',
    is_active: true
  });

  const [expandedDescription, setExpandedDescription] = useState<string | null>(null);
  const [expandedRules, setExpandedRules] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchAllActivities(filters);
      const activitiesData = Array.isArray(response) ? response : 
                           (response.data && Array.isArray(response.data)) ? response.data : 
                           (response.activities && Array.isArray(response.activities)) ? response.activities : [];
      setActivities(activitiesData);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const handleInputChange = (field: keyof ActivityForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Variant helper functions
  const addVariant = () => {
    const newVariant: ActivityVariant = {
      mode: 'shared',
      price_paise: 0,
      price_unit: 'per_person',
      currency: formData.currency,
      is_active: true
    };
    setVariants(prev => [...prev, newVariant]);
  };

  const patchVariant = (idx: number, patch: Partial<ActivityVariant>) => {
    setVariants(prev => prev.map((variant, i) => 
      i === idx ? { ...variant, ...patch } : variant
    ));
  };

  const removeVariant = (idx: number) => {
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name: string) => {
    handleInputChange('name', name);
    if (!editingActivity) {
      handleInputChange('slug', generateSlug(name));
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      name: '',
      description: '',
      duration_minutes: undefined,
      start_time: '',
      remarks: '',
      rules: '',
      picture_url: '',
      city: '',
      city_code: '',
      category: '',
      currency: 'INR',
      is_active: true,
      variants: []
    });
    setVariants([]);
    setEditingActivity(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    try {
      const payload = { ...formData, variants };
      if (editingActivity) {
        await updateActivity(editingActivity.id!, payload, file);
      } else {
        await createActivity(payload, file);
      }
  
      resetForm();
      setFile(undefined);
      loadActivities();
    } catch (error) {
    }
  };

  const handleEdit = async (activity: Activity) => {
    setEditingActivity(activity);
    setFormData({
      slug: activity.slug,
      name: activity.name,
      description: activity.description || '',
      duration_minutes: activity.duration_minutes,
      start_time: activity.start_time || '',
      remarks: activity.remarks || '',
      rules: activity.rules || '',
      picture_url: activity.picture_url || '',
      city: activity.city || '',
      city_code: activity.city_code || '',
      category: activity.category || '',
      currency: activity.currency as Currency,
      is_active: activity.is_active,
      variants: activity.variants || []
    });

    // Load full activity data to get variants
    try {
      const fullActivity = await getActivityFull(activity.id!);
      setVariants(fullActivity.variants || []);
    } catch (error) {
      setVariants(activity.variants || []);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        await deleteActivity(id);
        loadActivities();
      } catch (error) {
      }
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Layout>
      <div className="activities-page">
        <div className="activities-container">
        <div className="activities-form-section">
          <div className="form-header">
            <h2>{editingActivity ? 'Edit Activity' : 'Add New Activity'}</h2>
            {editingActivity && (
              <button className="cancel-btn" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="activity-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="Activity name"
                />
              </div>
              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  required
                  placeholder="activity-slug"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Activity description"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.duration_minutes || ''}
                  onChange={(e) => handleInputChange('duration_minutes', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="120"
                />
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleInputChange('start_time', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCityLocations}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => o.value}
                  onChange={(opt) => {
                    if (opt) {
                      handleInputChange('city', opt.label);
                      handleInputChange('city_code', opt.value);
                    } else {
                      handleInputChange('city', '');
                      handleInputChange('city_code', '');
                    }
                  }}
                  value={
                    formData.city
                      ? { label: formData.city, value: formData.city, type: 'city' }
                      : null
                  }
                  placeholder="Type a city name"
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  placeholder="Adventure"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => {
                  const newCurrency = e.target.value as Currency;
                  handleInputChange('currency', newCurrency);
                  // Update all variants to use the new currency
                  setVariants(prev => prev.map(variant => ({ ...variant, currency: newCurrency })));
                }}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div className="form-group">
              <label>Pricing Variants</label>
              <div className="variants-section">
                {variants.map((variant, idx) => (
                  <div key={idx} className="variant-row">
                    <div className="variant-field">
                      <label>Mode</label>
                      <select
                        value={variant.mode}
                        onChange={(e) => patchVariant(idx, { mode: e.target.value as 'shared' | 'private' | 'only_ticket' })}
                      >
                        <option value="shared">Shared</option>
                        <option value="private">Private</option>
                        <option value="only_ticket">Only Ticket</option>
                      </select>
                    </div>

                    <div className="variant-field">
                      <label>Pax Type</label>
                      <select
                        value={variant.pax_count ? 'exact' : 'range'}
                        onChange={(e) => {
                          if (e.target.value === 'exact') {
                            patchVariant(idx, { pax_count: 1, pax_min: undefined, pax_max: undefined });
                          } else {
                            patchVariant(idx, { pax_count: undefined, pax_min: 1, pax_max: 10 });
                          }
                        }}
                      >
                        <option value="exact">Exact count</option>
                        <option value="range">Range</option>
                      </select>
                    </div>

                    {variant.pax_count ? (
                      <div className="variant-field">
                        <label>Pax Count</label>
                        <input
                          type="number"
                          value={variant.pax_count}
                          onChange={(e) => patchVariant(idx, { pax_count: parseInt(e.target.value) || 1 })}
                          min="1"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="variant-field">
                          <label>Min Pax</label>
                          <input
                            type="number"
                            value={variant.pax_min || 1}
                            onChange={(e) => patchVariant(idx, { pax_min: parseInt(e.target.value) || 1 })}
                            min="1"
                          />
                        </div>
                        <div className="variant-field">
                          <label>Max Pax</label>
                          <input
                            type="number"
                            value={variant.pax_max || 10}
                            onChange={(e) => patchVariant(idx, { pax_max: parseInt(e.target.value) || 10 })}
                            min="1"
                          />
                        </div>
                      </>
                    )}

                    <div className="variant-field">
                      <label>Price Unit</label>
                      <select
                        value={variant.price_unit}
                        onChange={(e) => patchVariant(idx, { price_unit: e.target.value as PriceUnit })}
                      >
                        <option value="per_person">Per Person</option>
                        <option value="per_group">Per Group</option>
                      </select>
                    </div>

                    <div className="variant-field">
                      <label>Price (paise)</label>
                      <input
                        type="number"
                        value={variant.price_paise}
                        onChange={(e) => patchVariant(idx, { price_paise: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>

                    <div className="variant-field">
                      <label>Label</label>
                      <input
                        type="text"
                        value={variant.label || ''}
                        onChange={(e) => patchVariant(idx, { label: e.target.value })}
                        placeholder="Optional label"
                      />
                    </div>

                    <div className="variant-field">
                      <label>
                        <input
                          type="checkbox"
                          checked={variant.is_active}
                          onChange={(e) => patchVariant(idx, { is_active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>

                    <div className="variant-field">
                      <button
                        type="button"
                        onClick={() => removeVariant(idx)}
                        className="remove-variant-btn"
                        aria-label="Remove variant"
                        title="Remove variant"
                      >
                        <FiTrash2/>
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addVariant}
                  className="add-variant-btn"
                >
                  + Add Variant
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Activity Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? undefined)}
              />
            </div>

            <div className="form-group">
              <label>Rules</label>
              <textarea
                value={formData.rules}  
                onChange={(e) => handleInputChange('rules', e.target.value)}
                placeholder="Activity rules and guidelines"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                placeholder="Additional remarks"
                rows={2}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                />
                Active
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingActivity ? 'Update Activity' : 'Add Activity'}
              </button>
              {!editingActivity && (
                <button type="button" onClick={resetForm} className="reset-btn">
                  Reset
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="activities-list-section">
          <div className="list-header">
            <h2>Activities List</h2>
            <div className="filters">
              <input
                type="text"
                placeholder="Filter by city"
                value={filters.city}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                className="filter-input"
              />

              <input
                type="text"
                placeholder="Filter by category"
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="filter-input"
              />
              <select
                value={filters.is_active.toString()}
                onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                className="filter-select"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
                <option value="">All</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading activities...</div>
          ) : (
            <div className="activities-list">
              {(!activities || activities.length === 0) ? (
                <div className="no-activities">No activities found</div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className={`activity-card ${!activity.is_active ? 'inactive' : ''}`}>
                    <div className="activity-header">
                      <h3>{activity.name}</h3>
                      <div className="activity-actions">
                        <button onClick={() => handleEdit(activity)} className="edit-btn">
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(activity.id!)} 
                          className="delete-btn"
                          aria-label="Delete activity"
                          title="Delete activity"
                        >
                          <FiTrash2/>
                        </button>
                      </div>
                    </div>
                    
                    <div className="activity-details">
                      {activity.picture_url && (
                        <div className="activity-image">
                          <img 
                            src={activity.picture_url} 
                            alt={activity.name}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="activity-content">
                        <div className="activity-info">
                          <p><strong>Slug:</strong> {activity.slug}</p>
                          <p><strong>City:</strong> {activity.city || 'N/A'}</p>
                          <p><strong>City Code:</strong> {activity.city_code || 'N/A'}</p>
                          <p><strong>Category:</strong> {activity.category || 'N/A'}</p>
                          {activity.duration_minutes && (
                            <p><strong>Duration:</strong> {formatDuration(activity.duration_minutes)}</p>
                          )}
                          {activity.start_time && (
                            <p><strong>Start Time:</strong> {formatTime(activity.start_time)}</p>
                          )}
                          {activity.from_price_paise && (
                            <p><strong>From:</strong> ₹{Math.round(activity.from_price_paise / 100)} ({activity.currency})</p>
                          )}
                          {activity.variant_count && (
                            <p><strong>Variants:</strong> {activity.variant_count}</p>
                          )}
                        </div>
                        
                        {activity.description && (
                          <div className="activity-description">
                            <p><strong>Description:</strong> 
                              {activity.description.length > 70 ? (
                                <>
                                  {activity.description.substring(0, 70)}...
                                  <button 
                                    type="button" 
                                    className="show-more-btn"
                                    onClick={() => {
                                      if (activity.id) {
                                        setExpandedDescription(expandedDescription === activity.id ? null : activity.id);
                                      }
                                    }}
                                  >
                                    {expandedDescription === activity.id ? 'Show less' : 'Show more'}
                                  </button>
                                  {expandedDescription === activity.id && (
                                    <span className="full-text">{activity.description.substring(70)}</span>
                                  )}
                                </>
                              ) : (
                                activity.description
                              )}
                            </p>
                          </div>
                        )}
                        
                        {activity.rules && (
                          <div className="activity-rules">
                            <p><strong>Rules:</strong> 
                              {activity.rules.length > 70 ? (
                                <>
                                  {activity.rules.substring(0, 70)}...
                                  <button 
                                    type="button" 
                                    className="show-more-btn"
                                    onClick={() => {
                                      if (activity.id) {
                                        setExpandedRules(expandedRules === activity.id ? null : activity.id);
                                      }
                                    }}
                                  >
                                    {expandedRules === activity.id ? 'Show less' : 'Show more'}
                                  </button>
                                  {expandedRules === activity.id && (
                                    <span className="full-text">{activity.rules.substring(70)}</span>
                                  )}
                                </>
                              ) : (
                                activity.rules
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default ActivitiesPage;
